import { BellRing, House, type LucideIcon, ScrollText, UsersRound } from 'lucide-react';
import { type ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { PermissionGate } from '@/components/auth/permission-gate';
import { CreateReminderFab } from '@/components/layout/create-reminder-fab';
import { useFeedback } from '@/hooks/use-feedback';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';

const navItems: { to: string; icon: LucideIcon; labelKey: string; end: boolean }[] = [
  { to: '/', icon: House, labelKey: 'nav.home', end: true },
  { to: '/reminders', icon: BellRing, labelKey: 'nav.reminders', end: false },
  { to: '/chats', icon: UsersRound, labelKey: 'nav.chats', end: false },
  { to: '/logs', icon: ScrollText, labelKey: 'nav.logs', end: false },
];

function NavItem({
  to,
  icon: Icon,
  label,
  end,
  onSelect,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  end: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onSelect}
      className={({ isActive }) =>
        cn(
          'studio-nav-item tap-target pressable relative flex flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 text-[10px] font-medium transition-colors',
          isActive && 'studio-nav-item-active',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <span
              className="studio-nav-marker absolute inset-x-3 inset-y-1.5 rounded-xl"
              aria-hidden
            />
          ) : null}
          <Icon
            className={cn(
              'studio-nav-icon relative h-[22px] w-[22px]',
              isActive && 'studio-nav-icon-selected',
            )}
            strokeWidth={1.65}
            aria-hidden
          />
          <span className="sr-only">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export function BottomNav(): ReactElement {
  const { t } = useI18n();
  const { select } = useFeedback();

  return (
    <nav
      className="studio-dock-wrap fixed right-0 bottom-0 left-0 z-20 mx-auto max-w-3xl px-3"
      style={{
        paddingBottom: 'var(--safe-area-inset-bottom)',
        height: 'calc(var(--bottom-nav-height) + var(--safe-area-inset-bottom))',
      }}
    >
      <div className="studio-dock mx-auto mt-2 grid h-[calc(var(--bottom-nav-height)-1rem)] max-w-lg grid-cols-5 items-center px-1 backdrop-blur-xl">
        {navItems.slice(0, 2).map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={t(item.labelKey)}
            end={item.end}
            onSelect={select}
          />
        ))}

        <PermissionGate
          permission="reminders:write"
          fallback={
            <div
              className="flex justify-center"
              aria-hidden
            />
          }
        >
          <div className="flex justify-center">
            <CreateReminderFab />
          </div>
        </PermissionGate>

        {navItems.slice(2).map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={t(item.labelKey)}
            end={item.end}
            onSelect={select}
          />
        ))}
      </div>
    </nav>
  );
}
